import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import type { IndividualScore } from "../types";

/**
 * Hook to fetch individual user scores based on their contributions
 */
export function useIndividualScores() {
  const { activeWeekend } = useAuth();
  const [individuals, setIndividuals] = useState<IndividualScore[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch individual scores from score_history.awarded_to
   */
  const fetchIndividuals = useCallback(async () => {
    if (!activeWeekend?.id) {
      setIndividuals([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // First, get all teams for this weekend to filter score history
      const { data: teams, error: teamsError } = await supabase
        .from("teams")
        .select("id")
        .eq("weekend_id", activeWeekend.id);

      if (teamsError) throw teamsError;

      if (!teams || teams.length === 0) {
        setIndividuals([]);
        return;
      }

      const teamIds = teams.map((t) => t.id);

      // Get score history entries that have awarded_to set
      const { data: scoreData, error: scoreError } = await supabase
        .from("score_history")
        .select(`
          awarded_to,
          points,
          team_id
        `)
        .in("team_id", teamIds)
        .not("awarded_to", "is", null);

      if (scoreError) throw scoreError;

      // Group by user and sum points
      const userPointsMap: Record<
        string,
        { total_points: number; team_id: string }
      > = {};
      (scoreData || []).forEach((entry) => {
        if (!entry.awarded_to) return;
        if (!userPointsMap[entry.awarded_to]) {
          userPointsMap[entry.awarded_to] = {
            total_points: 0,
            team_id: entry.team_id,
          };
        }
        userPointsMap[entry.awarded_to].total_points += entry.points;
      });

      const userIds = Object.keys(userPointsMap);

      if (userIds.length === 0) {
        setIndividuals([]);
        return;
      }

      // Fetch user profiles
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("id, full_name, nickname, avatar_url")
        .in("id", userIds);

      if (profilesError) throw profilesError;

      // Fetch team info for team names
      const { data: teamData, error: teamDataError } = await supabase
        .from("teams")
        .select("id, name, color")
        .in("id", teamIds);

      if (teamDataError) throw teamDataError;

      const teamMap: Record<string, { name: string; color: string | null }> = {};
      (teamData || []).forEach((t) => {
        teamMap[t.id] = { name: t.name, color: t.color };
      });

      // Build individual scores list
      const individualsList: IndividualScore[] = (profiles || [])
        .map((profile) => {
          const userData = userPointsMap[profile.id];
          const teamInfo = userData?.team_id ? teamMap[userData.team_id] : null;

          return {
            user_id: profile.id,
            full_name: profile.full_name,
            nickname: profile.nickname,
            avatar_url: profile.avatar_url,
            team_id: userData?.team_id || null,
            team_name: teamInfo?.name || null,
            team_color: teamInfo?.color || null,
            total_points: userData?.total_points || 0,
            rank: 0, // Will be set after sorting
          };
        })
        .filter((i) => i.total_points > 0)
        .sort((a, b) => b.total_points - a.total_points);

      // Assign ranks
      individualsList.forEach((ind, index) => {
        ind.rank = index + 1;
      });

      setIndividuals(individualsList);
    } catch (err) {
      console.error("Error fetching individual scores:", err);
      setError(
        err instanceof Error ? err.message : "Failed to fetch individual scores"
      );
    } finally {
      setLoading(false);
    }
  }, [activeWeekend?.id]);

  useEffect(() => {
    fetchIndividuals();
  }, [fetchIndividuals]);

  return {
    individuals,
    loading,
    error,
    refresh: fetchIndividuals,
    topIndividuals: individuals.slice(0, 10),
  };
}

